// Geometry of the observation-window timeline (docs/design-system.md §0.8,
// WindowTimeline). Dates are local dates in the product's time zone
// (YYYY-MM-DD); positions are percentages of the visible range. Plain
// TypeScript so it is tested without a DOM.

const DAY = 86_400_000;
const at = (date: string) => Date.parse(`${date}T00:00:00Z`);

/** Whole days from `from` to `to`; both are local dates. */
export const daysBetween = (from: string, to: string) => Math.round((at(to) - at(from)) / DAY);

export interface Range { from: string; to: string }

/** Where a date sits in the range, 0–100, or null outside it. The range's last day ends at 100. */
export function position(range: Range, date: string): number | null {
  const span = daysBetween(range.from, range.to) + 1;
  if (span <= 0) return null;
  const offset = daysBetween(range.from, date);
  if (offset < 0 || offset > span) return null;
  return (offset / span) * 100;
}

export interface Bar {
  /** Start and width of the whole window, clipped to the range. */
  left: number;
  width: number;
  /** The share of the window already behind today, 0–100 of its own width. */
  elapsed: number;
}

/**
 * The bar of one window: start date and length in days. A window that has not
 * started has no bar (null), and one entirely outside the range has none either.
 */
export function windowBar(range: Range, start: string | null, days: number | null, today: string): Bar | null {
  if (!start || days == null || days <= 0) return null;
  const span = daysBetween(range.from, range.to) + 1;
  if (span <= 0) return null;
  const startOffset = daysBetween(range.from, start);
  const endOffset = startOffset + days;
  const visibleStart = Math.max(0, startOffset);
  const visibleEnd = Math.min(span, endOffset);
  if (visibleEnd <= visibleStart) return null;
  const done = Math.min(days, Math.max(0, daysBetween(start, today)));
  return {
    left: (visibleStart / span) * 100,
    width: ((visibleEnd - visibleStart) / span) * 100,
    elapsed: (done / days) * 100,
  };
}

/** Evenly spaced tick dates across the range, first and last included. */
export function ticks(range: Range, count: number): string[] {
  const span = daysBetween(range.from, range.to);
  if (span < 0 || count < 2) return [range.from];
  const out = new Set<string>();
  for (let i = 0; i < count; i++) {
    const offset = Math.round((span * i) / (count - 1));
    out.add(new Date(at(range.from) + offset * DAY).toISOString().slice(0, 10));
  }
  return [...out];
}
